import type { VisibilityFilter } from "@albertine/types";
import type { Theme } from "@albertine/types/openapi/lmt";
import type { ThemeLocation, ThemePreference } from "@albertine/types/theme";
import { cityGuideKey, cityguideKeyDelimiter } from "../common/utils/catalog.util";
import { isString } from "../common/utils/string.util";
import { takeIfAnyPredicateMatches } from "./filter.util";

export const takeIfNameMatches =
    (nameSearchTerm: string | undefined) =>
    (theme: Theme): boolean => {
        const nameMatch = nameSearchTerm
            ? takeIfAnyPredicateMatches<Theme>([theme], nameSearchTerm, [(t) => t.name])
                  .length !== 0
            : true;
        return nameMatch;
    };

export const takeIfLocationMatches =
    (location: ThemeLocation | undefined) =>
    (theme: Theme): boolean => {
        const locationMatch = location
            ? theme.location.some((it) =>
                  location.city
                      ? it.city === location.city &&
                        it.country === location.country &&
                        it.countryCode === location.countryCode
                      : it.country === location.country &&
                        it.countryCode === location.countryCode,
              )
            : true;
        return locationMatch;
    };

export const takeIfCityguideLocationMatches =
    (location: ThemeLocation | undefined) =>
    (theme: Theme): boolean => {
        if (!location) {
            return true;
        }

        const fullMatch = (key: string, city: string, countryCode: string): boolean =>
            key ===
            cityGuideKey({
                city,
                countryCode,
            });
        const countryMatch = (key: string, countryCode: string): boolean => {
            const keySplit = key.split(cityguideKeyDelimiter);
            return keySplit[1] ? keySplit[1] === countryCode.toLowerCase() : false;
        };

        const matchingCityGuideLocation =
            location.city && location.countryCode
                ? fullMatch(theme.id, location.city, location.countryCode)
                : countryMatch(theme.id, location.countryCode);

        return matchingCityGuideLocation;
    };

export const takeIfThemePreferenceMatches =
    (preferences: ThemePreference[] | undefined) =>
    (theme: Theme): boolean => {
        if (!preferences || preferences?.length === 0) {
            return true;
        }

        return preferences.filter((it) => theme.preferences.includes(it)).length !== 0;
    };

export const filterThemeByPublished =
    (published: VisibilityFilter | undefined) =>
    (theme: Theme): boolean => {
        if (!published) {
            return true;
        }

        const themeIsPublished = isString(theme.publishedAt);

        return (
            (themeIsPublished && published === "published") ||
            (!themeIsPublished && published === "unpublished")
        );
    };
